import { tryBrowserStorage } from '@cloud-ui/utils/browserStorage';
import { clearPendingInvite, discardCapturedInvite } from '@cloud-ui/utils/pendingInvite';

/** Session writes must finish and notify peers before a peer change can unload this tab. */
export const ORGANIZATION_SESSION_MUTATION_KEY = ['session', 'organization'] as const;

const ORGANIZATION_CHANGE_EVENT = 'auth_organization_event';
const LOGOUT_EVENT = 'auth_logout_event';
const SESSION_REVIEW_EVENT = 'auth_session_review';

/** Request local, explicit recovery without ending this or another tab's session. */
export function requestSessionReview(): void {
  window.dispatchEvent(new Event(SESSION_REVIEW_EVENT));
}

/** Listen only in this document; uncertain storage never establishes a cross-tab logout. */
export function subscribeToSessionReview(onReview: () => void): () => void {
  window.addEventListener(SESSION_REVIEW_EVENT, onReview);
  return () => window.removeEventListener(SESSION_REVIEW_EVENT, onReview);
}

type SessionChange = typeof ORGANIZATION_CHANGE_EVENT | typeof LOGOUT_EVENT;
// Other channel objects in this tab also receive messages; the sender keeps its own navigation.
const sessionChangeSender = Math.random().toString(36);
let localSessionGeneration = 0;

function openSessionChannel(): BroadcastChannel | undefined {
  try {
    return new BroadcastChannel(ORGANIZATION_CHANGE_EVENT);
  } catch {
    return undefined;
  }
}

/** Publish a session change, falling back when browser storage is unavailable. */
function announceSessionChange(type: SessionChange): void {
  const sent = tryBrowserStorage(() => {
    localStorage.setItem(type, Date.now().toString());
    return true;
  });
  tryBrowserStorage(() => localStorage.removeItem(type));
  if (!sent) {
    const channel = openSessionChannel();
    try {
      channel?.postMessage({ type, sender: sessionChangeSender });
    } catch {
      // Session cleanup still completes if both browser transports are unavailable.
    } finally {
      channel?.close();
    }
  }
}

/** Call after the server moves this browser's session into another organization. */
export const announceOrganizationChange = () => announceSessionChange(ORGANIZATION_CHANGE_EVENT);

/** Listen for session changes from other tabs through either available browser transport. */
export function subscribeToSessionChanges(onChange: (type: SessionChange) => void): () => void {
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === LOGOUT_EVENT || (event.key === ORGANIZATION_CHANGE_EVENT && event.newValue)) {
      onChange(event.key);
    }
  };
  const channel = openSessionChannel();
  if (channel) {
    channel.onmessage = ({ data }: MessageEvent) => {
      if (
        data?.sender !== sessionChangeSender &&
        (data?.type === LOGOUT_EVENT || data?.type === ORGANIZATION_CHANGE_EVENT)
      ) {
        onChange(data.type);
      }
    };
  }
  window.addEventListener('storage', handleStorageChange);
  return () => {
    channel?.close();
    window.removeEventListener('storage', handleStorageChange);
  };
}

/**
 * Track whether the session that started an async operation has ended. Peer organization
 * selections keep the same session; local or peer logout invalidates it, even after unmount.
 * Dispose after the operation's callbacks finish to release the peer-event listener.
 */
export function trackAuthSession() {
  const generation = localSessionGeneration;
  let peerLoggedOut = false;
  const dispose = subscribeToSessionChanges((type) => {
    if (type === LOGOUT_EVENT) {
      peerLoggedOut = true;
    }
  });
  return {
    isCurrent: () => !peerLoggedOut && generation === localSessionGeneration,
    dispose,
  };
}

/** Clear session-owned browser state before an expired session redirects, or after logout succeeds. */
export const clearSessionAuthStorage = ({ preserveInvitation = false } = {}) => {
  localSessionGeneration += 1;
  tryBrowserStorage(() => localStorage.removeItem('used_codes'));
  if (!preserveInvitation) {
    clearPendingInvite();
    discardCapturedInvite();
  }
  announceSessionChange(LOGOUT_EVENT);
};
